/**
 * Zod-схемы модуля `links` — реестр подключённых сервисов (docs/DOMAIN-MODEL.md §5).
 *
 * Инвариант №5: строка `service_links` появляется ТОЛЬКО после реального входа.
 * Account chooser показывает «Продолжить как …» тихим запросом к сессии и НЕ
 * создаёт связь, поэтому в контракте нет ни одной процедуры «подключить сервис»:
 * подключение — следствие входа, а не отдельное действие пользователя.
 *
 * Отключение (`disconnect`) обратимо: следующий вход снова активирует связь.
 * Необратимое — `requestDataDeletion`: просьба сервису стереть локальную копию.
 */
import { z } from "zod";
import {
	IsoDateTimeSchema,
	paginated,
	ServiceIdSchema,
	UuidV7Schema,
} from "../_shared/primitives.js";

/**
 * Что именно сервис получает из профиля (макет 3d: «name, photo, email, age group»).
 * Значения — ключи i18n и одновременно OIDC-claims; список расширяем без миграции.
 */
export const SERVICE_DATA_SCOPES = [
	"name",
	"nickname",
	"picture",
	"email",
	"age_group",
	"locale",
	"timezone",
	"gender",
	"programmes",
	"points_balance",
	"entitlements",
] as const;

export const ServiceDataScopeSchema = z.enum(SERVICE_DATA_SCOPES);

/** Состояние связи. `disconnected` — сервис перестал получать профиль. */
export const ServiceLinkStateSchema = z.discriminatedUnion("status", [
	z.object({
		status: z.literal("active"),
		activatedAt: IsoDateTimeSchema,
		lastSeenAt: IsoDateTimeSchema,
	}),
	z.object({
		status: z.literal("disconnected"),
		activatedAt: IsoDateTimeSchema,
		disconnectedAt: IsoDateTimeSchema,
	}),
]);

/**
 * Почему запрос ушёл в ручную очередь. Значения — `service_data_deletions.
 * escalation_reason`; строка, а не enum, потому что список причин пополняется
 * доставкой, а не релизом схемы.
 */
export const ServiceDataDeletionEscalationSchema = z.string().min(1).max(64);

/**
 * Просьба сервису удалить локальную копию данных (GDPR ст. 17).
 * Молчаливого «удалили» не бывает: провал виден и в статусе, и в очереди админки.
 *
 * `escalated` — отдельное состояние, а не разновидность `failed`: у него нет
 * следующей попытки. Автоматика больше не пробует (`escalated_at` выводит
 * строку из подметания), обязательство исполняет человек, и показывать это
 * как «не удалось, попыток 20» — значит обещать повтор, которого не будет.
 * Колонки `escalated_at` / `escalation_reason` существуют с миграции 0024, но
 * до 21.08.2026 не доходили даже до маппера (грабли №3, «mapper drop»).
 */
export const ServiceDataDeletionSchema = z.discriminatedUnion("state", [
	z.object({
		state: z.literal("pending"),
		requestId: UuidV7Schema,
		requestedAt: IsoDateTimeSchema,
		attempts: z.number().int().nonnegative(),
	}),
	z.object({
		state: z.literal("acked"),
		requestId: UuidV7Schema,
		requestedAt: IsoDateTimeSchema,
		completedAt: IsoDateTimeSchema,
	}),
	z.object({
		state: z.literal("failed"),
		requestId: UuidV7Schema,
		requestedAt: IsoDateTimeSchema,
		attempts: z.number().int().positive(),
		lastErrorCode: z.string().min(1).max(64),
	}),
	z.object({
		state: z.literal("escalated"),
		requestId: UuidV7Schema,
		requestedAt: IsoDateTimeSchema,
		/** Когда запрос вывели из автоматики. */
		escalatedAt: IsoDateTimeSchema,
		attempts: z.number().int().nonnegative(),
		/** `no_endpoint` | `unknown_subject` | `rejected` и то, что добавится. */
		escalationReason: ServiceDataDeletionEscalationSchema,
	}),
]);

export const ServiceLinkSchema = z.object({
	serviceId: ServiceIdSchema,
	state: ServiceLinkStateSchema,
	/** Какие поля профиля уходят сервису — показывается строкой под названием. */
	scopes: z.array(ServiceDataScopeSchema),
	/** Есть ли у сервиса собственные платные уровни (для строки «Subscription: Plus»). */
	hasTiers: z.boolean(),
	/** Активный запрос на удаление данных; `null` — запроса не было. */
	deletion: ServiceDataDeletionSchema.nullable(),
});

// ───────────────────────────── links.list ─────────────────────────────

export const LinkListInputSchema = z.object({
	/** По умолчанию отключённые связи скрыты — раздел «Сервисы» показывает активные. */
	includeDisconnected: z.boolean().default(false),
});

export const LinkListOutputSchema = paginated(ServiceLinkSchema);

// ─────────────────────────── links.disconnect ─────────────────────────

export const LinkDisconnectInputSchema = z.object({ serviceId: ServiceIdSchema });

export const LinkDisconnectOutputSchema = z.object({
	link: ServiceLinkSchema,
	/**
	 * Отключение НЕ удаляет локальный аккаунт в сервисе — для этого отдельная
	 * процедура. Клиент обязан сказать это пользователю явно.
	 */
	localAccountRemains: z.literal(true),
});

// ────────────────────── links.requestDataDeletion ─────────────────────

export const LinkDataDeletionInputSchema = z.object({
	serviceId: ServiceIdSchema,
	/** Необратимое действие — подтверждение обязательно на уровне контракта. */
	confirm: z.literal(true),
	reasonCode: z.string().min(1).max(64).nullish(),
});

export const LinkDataDeletionOutputSchema = z.object({
	serviceId: ServiceIdSchema,
	deletion: ServiceDataDeletionSchema,
	/**
	 * Финансовые и аудиторские записи анонимизируются, а не удаляются
	 * (инвариант №11) — на витрине это отдельная строка, а не мелкий шрифт.
	 */
	financialRecordsAnonymized: z.literal(true),
});
